import { createTheme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'

const brandColors = {
    brownPrimary: '#675944',
    brownLight: '#8B7355',
    brownDark: '#574732',
    brownSubtle: '#A89A84',
    brownBackground: '#FDFDFB',
    charcoal: '#1A1A1A',
    charcoalMedium: '#2A2A2A',
    warmWhite: '#FDFDFB',
    warmGrey: '#FAF8F5',
    lightGrey: '#F5F2ED',
    textSecondary: '#666666',
    textTertiary: '#4a4a4a',
    errorRed: '#D32F2F',
    warningAmber: '#FF9800',
    successGreen: '#2E7D32',
    infoBlue: '#1976D2'
}

export const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: brandColors.brownPrimary,
            light: alpha(brandColors.brownPrimary, 0.7),
            dark: brandColors.brownDark,
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: brandColors.brownLight,
            light: alpha(brandColors.brownLight, 0.7),
            dark: alpha(brandColors.brownLight, 0.9),
            contrastText: brandColors.charcoal,
        },
        error: {
            main: brandColors.errorRed,
            light: alpha(brandColors.errorRed, 0.7),
            dark: alpha(brandColors.errorRed, 0.9),
        },
        warning: {
            main: brandColors.warningAmber,
            light: alpha(brandColors.warningAmber, 0.7),
            dark: alpha(brandColors.warningAmber, 0.9),
        },
        success: {
            main: brandColors.successGreen,
            light: alpha(brandColors.successGreen, 0.7),
            dark: alpha(brandColors.successGreen, 0.9),
        },
        info: {
            main: brandColors.infoBlue,
            light: alpha(brandColors.infoBlue, 0.7),
            dark: alpha(brandColors.infoBlue, 0.9),
        },
        background: {
            default: brandColors.warmWhite,
            paper: '#FFFFFF',
            subtle: brandColors.warmGrey,
            accent: brandColors.lightGrey,
        },
        text: {
            primary: brandColors.charcoal,
            secondary: brandColors.textSecondary,
            tertiary: brandColors.textTertiary,
            disabled: alpha(brandColors.charcoal, 0.38),
        },
        grey: {
            50: '#FAFAFA',
            100: '#F5F5F5',
            200: '#EEEEEE',
            300: '#E0E0E0',
            400: '#BDBDBD',
            500: '#9E9E9E',
            600: '#757575',
            700: '#616161',
            800: '#424242',
            900: '#212121',
        },
        brand: {
            brownPrimary: brandColors.brownPrimary,
            brownLight: brandColors.brownLight,
            brownDark: brandColors.brownDark,
            brownSubtle: brandColors.brownSubtle,
            charcoal: brandColors.charcoal,
            warmWhite: brandColors.warmWhite,
            warmGrey: brandColors.warmGrey,
        }
    },
    typography: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        h1: {
            fontFamily: '"Inter", sans-serif',
            fontWeight: 300,
            fontSize: '4rem',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: brandColors.charcoal,
        },
        h2: {
            fontFamily: '"Inter", sans-serif',
            fontWeight: 300,
            fontSize: '3rem',
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
            color: brandColors.charcoal,
        },
        h3: {
            fontFamily: '"Inter", sans-serif',
            fontWeight: 300,
            fontSize: '2.25rem',
            lineHeight: 1.3,
            color: brandColors.charcoal,
        },
        h4: {
            fontFamily: '"Inter", sans-serif',
            fontWeight: 500,
            fontSize: '1.8rem',
            lineHeight: 1.4,
            color: brandColors.charcoal,
        },
        h5: {
            fontFamily: '"Inter", sans-serif',
            fontWeight: 500,
            fontSize: '1.5rem',
            lineHeight: 1.4,
            color: brandColors.charcoal,
        },
        h6: {
            fontFamily: '"Inter", sans-serif',
            fontWeight: 500,
            fontSize: '1.25rem',
            lineHeight: 1.5,
            color: brandColors.charcoal,
        },
        subtitle1: {
            fontSize: '1.125rem',
            lineHeight: 1.6,
            fontWeight: 400,
            color: brandColors.textSecondary,
        },
        subtitle2: {
            fontSize: '1rem',
            lineHeight: 1.6,
            fontWeight: 500,
            color: brandColors.textSecondary,
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.7,
            letterSpacing: '0.01em',
            color: brandColors.textSecondary,
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.6,
            letterSpacing: '0.01em',
            color: brandColors.textSecondary,
        },
        caption: {
            fontSize: '0.75rem',
            lineHeight: 1.5,
            letterSpacing: '0.03em',
            color: brandColors.textTertiary,
        },
        button: {
            fontWeight: 500,
            textTransform: 'none',
            letterSpacing: '0.02em',
            fontSize: '1rem',
        },
    },
    spacing: 8,
    shape: {
        borderRadius: 12,
    },
    shadows: [
        'none',
        '0 2px 8px rgba(26, 26, 26, 0.04)',
        '0 4px 12px rgba(26, 26, 26, 0.06)',
        '0 6px 16px rgba(26, 26, 26, 0.08)',
        '0 8px 20px rgba(26, 26, 26, 0.10)',
        '0 10px 24px rgba(26, 26, 26, 0.12)',
        '0 12px 28px rgba(26, 26, 26, 0.14)',
        '0 16px 32px rgba(26, 26, 26, 0.16)',
        '0 20px 36px rgba(26, 26, 26, 0.18)',
        '0 24px 40px rgba(26, 26, 26, 0.20)',
        '0 28px 44px rgba(26, 26, 26, 0.22)',
        '0 32px 48px rgba(26, 26, 26, 0.24)',
        '0 36px 52px rgba(26, 26, 26, 0.26)',
        '0 40px 56px rgba(26, 26, 26, 0.28)',
        '0 44px 60px rgba(26, 26, 26, 0.30)',
        '0 48px 64px rgba(26, 26, 26, 0.32)',
        '0 52px 68px rgba(26, 26, 26, 0.34)',
        '0 56px 72px rgba(26, 26, 26, 0.36)',
        '0 60px 76px rgba(26, 26, 26, 0.38)',
        '0 64px 80px rgba(26, 26, 26, 0.40)',
        '0 68px 84px rgba(26, 26, 26, 0.42)',
        '0 72px 88px rgba(26, 26, 26, 0.44)',
        '0 76px 92px rgba(26, 26, 26, 0.46)',
        '0 80px 96px rgba(26, 26, 26, 0.48)',
        '0 84px 100px rgba(26, 26, 26, 0.50)'
    ],
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '1rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    boxShadow: 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 25px ${alpha(brandColors.brownPrimary, 0.25)}`,
                    },
                },
                contained: {
                    backgroundColor: brandColors.brownPrimary,
                    color: '#FFFFFF',
                    '&:hover': {
                        backgroundColor: brandColors.brownDark,
                        boxShadow: `0 8px 25px ${alpha(brandColors.brownPrimary, 0.35)}`,
                    },
                },
                outlined: {
                    borderColor: brandColors.brownPrimary,
                    color: brandColors.brownPrimary,
                    borderWidth: '1px',
                    '&:hover': {
                        backgroundColor: alpha(brandColors.brownPrimary, 0.08),
                        borderColor: brandColors.brownDark,
                    },
                },
                text: {
                    color: brandColors.brownPrimary,
                    '&:hover': {
                        backgroundColor: alpha(brandColors.brownPrimary, 0.08),
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(26, 26, 26, 0.08)',
                    border: `1px solid ${alpha(brandColors.brownPrimary, 0.1)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 40px rgba(26, 26, 26, 0.12)',
                    },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: alpha(brandColors.warmWhite, 0.95),
                    backdropFilter: 'blur(20px)',
                    boxShadow: `0 1px 8px ${alpha(brandColors.brownPrimary, 0.08)}`,
                    borderBottom: `1px solid ${alpha(brandColors.brownPrimary, 0.1)}`,
                    color: brandColors.charcoal,
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: alpha(brandColors.warmWhite, 0.7),
                        '& fieldset': {
                            borderColor: alpha(brandColors.brownPrimary, 0.2),
                        },
                        '&:hover fieldset': {
                            borderColor: alpha(brandColors.brownPrimary, 0.3),
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: brandColors.brownPrimary,
                            borderWidth: '2px',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: brandColors.textSecondary,
                        '&.Mui-focused': {
                            color: brandColors.brownPrimary,
                        },
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: '20px',
                    fontWeight: 500,
                },
                outlined: {
                    borderColor: brandColors.brownPrimary,
                    color: brandColors.brownPrimary,
                    '&:hover': {
                        backgroundColor: alpha(brandColors.brownPrimary, 0.08),
                    },
                },
                filled: {
                    backgroundColor: brandColors.brownPrimary,
                    color: '#FFFFFF',
                    '&:hover': {
                        backgroundColor: brandColors.brownDark,
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'scale(1.05)',
                    },
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    '&.brand-brown': {
                        color: brandColors.brownPrimary,
                    },
                    '&.brand-charcoal': {
                        color: brandColors.charcoal,
                    },
                },
            },
        },
    },
    custom: {
        gradients: {
            primary: `linear-gradient(135deg, ${brandColors.brownPrimary} 0%, ${brandColors.brownLight} 100%)`,
            background: `linear-gradient(135deg, ${brandColors.warmWhite} 0%, ${brandColors.warmGrey} 50%, ${brandColors.lightGrey} 100%)`,
            dark: `linear-gradient(135deg, ${brandColors.charcoal} 0%, ${brandColors.charcoalMedium} 100%)`,
            brownAccent: `linear-gradient(120deg, ${alpha(brandColors.brownPrimary, 0.2)} 0%, ${alpha(brandColors.brownPrimary, 0.1)} 100%)`,
        },
        shadows: {
            soft: '0 2px 12px rgba(26, 26, 26, 0.06)',
            medium: '0 8px 32px rgba(26, 26, 26, 0.12)',
            strong: '0 20px 60px rgba(26, 26, 26, 0.15)',
            brown: `0 4px 20px ${alpha(brandColors.brownPrimary, 0.25)}`,
            brownStrong: `0 8px 32px ${alpha(brandColors.brownPrimary, 0.35)}`,
        },
        animations: {
            smooth: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            bouncy: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
            gentle: 'all 0.4s ease-in-out',
        },
        breakpoints: {
            xs: '0px',
            sm: '600px',
            md: '960px',
            lg: '1280px',
            xl: '1920px',
        },
    },
})